/**
 * Bağlantı ve kuyruk göstergeleri.
 *
 * KK-312 pazarlık dışı: "Ekranın sabit bir köşesinde BAĞLANTI DURUMU ve
 * GÖNDERİLMEMİŞ SATIŞ SAYISI HER ZAMAN görünür." Bu bileşen kasada asla
 * koşullu render edilmez — offline'ken gizlenmesi değil, offline'ken
 * BELİRGİNLEŞMESİ gerekir.
 */
import type { ReactNode } from "react";
import { Rozet } from "./temel.js";

export type BaglantiDurumu = "cevrimici" | "cevrimdisi" | "senkronlaniyor";

export function BaglantiCubugu({
  durum,
  kuyruk,
  sonSenkron,
  depolamaIzni = true,
  bas,
  ek,
}: {
  durum: BaglantiDurumu;
  /** Gönderilmemiş satış sayısı */
  kuyruk: number;
  sonSenkron?: Date | string | null;
  /** AK-12: navigator.storage.persist() reddedildiyse KALICI uyarı bandı */
  depolamaIzni?: boolean;
  /** Bandın sol ucu: cihaz kimliği. Durum göstergesinden ÖNCE gelir. */
  bas?: ReactNode;
  ek?: ReactNode;
}) {
  const zaman =
    sonSenkron &&
    (typeof sonSenkron === "string"
      ? new Date(sonSenkron)
      : sonSenkron
    ).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });

  const cevrimdisi = durum === "cevrimdisi";

  return (
    <div
      className={[
        "flex min-h-satir flex-wrap items-center gap-3 border-b px-4 py-1.5 text-sm",
        cevrimdisi
          ? "border-uyari-cizgi bg-uyari-yumusak text-uyari"
          : "border-cetvel-silik bg-kagit-2 text-murekkep-2",
      ].join(" ")}
      role="status"
      aria-live="polite"
    >
      {bas && (
        <span className="inline-flex items-center gap-3">
          {bas}
          <span aria-hidden="true" className="h-4 w-px bg-cetvel" />
        </span>
      )}

      <span className="inline-flex items-center gap-2 font-satir">
        {/* Durum metni HER ZAMAN yazılır; nokta yalnızca ikinci ipucudur. */}
        <span
          aria-hidden="true"
          className={[
            "inline-block h-2 w-2 rounded-tam",
            cevrimdisi
              ? "bg-etiket-sari"
              : durum === "senkronlaniyor"
                ? "animate-nabiz bg-etiket-indigo"
                : "bg-etiket-yesil",
          ].join(" ")}
        />
        {cevrimdisi
          ? "Çevrimdışı"
          : durum === "senkronlaniyor"
            ? "Senkronlanıyor"
            : "Çevrimiçi"}
      </span>

      {/* Kuyruk sayacı: 0 olsa bile GÖRÜNÜR kalır. Görünmeyen sayaç,
          "acaba gitti mi" sorusunu kasiyerin zihnine bırakır. */}
      <span className="inline-flex items-center gap-1.5">
        <span className="text-murekkep-3">Gönderilmemiş satış:</span>
        <strong className="rakam font-mono font-satir tabular-nums text-murekkep">
          {kuyruk}
        </strong>
        {kuyruk > 0 && (
          <Rozet tur={cevrimdisi ? "uyari" : "bilgi"}>bekliyor</Rozet>
        )}
      </span>

      {zaman && (
        <span className="text-murekkep-3">
          Son senkron: <span className="rakam font-mono">{zaman}</span>
        </span>
      )}

      {!depolamaIzni && (
        <span className="inline-flex items-center gap-1.5 font-satir text-hata">
          <Rozet tur="hata">depolama izni yok</Rozet>
          Satışlar kalıcı saklanamıyor — yönetime bildirin
        </span>
      )}

      <span className="ml-auto">{ek}</span>
    </div>
  );
}

/**
 * KK-544: fiş durumu göstergesi — açık/kapalı, agent bağlı mı, son yazdırma.
 * KK-543: yazıcı arızası satışı ASLA engellemez; bu gösterge yalnızca bilgilendirir.
 */
export function FisDurumu({
  politika,
  acik,
  agentBagli,
  sonYazdirmaBasarili,
  degistir,
}: {
  /** 1 kapalı, 2 opsiyonel, 3 zorunlu (KK-542) */
  politika: number;
  acik: boolean;
  agentBagli: boolean;
  sonYazdirmaBasarili?: boolean | null;
  degistir?: (yeni: boolean) => void;
}) {
  // KK-542 / AK-25: görevlinin anahtarı YALNIZCA politika "opsiyonel" iken çalışır.
  const anahtarCalisir = politika === 2;
  const aciklama =
    politika === 3
      ? "Bu noktada fiş zorunlu"
      : politika === 1
        ? "Bu noktada fiş kapalı"
        : undefined;

  if (!agentBagli && politika === 1) return null;

  return (
    <div className="inline-flex items-center gap-2">
      <button
        type="button"
        disabled={!anahtarCalisir || !agentBagli}
        onClick={() => degistir?.(!acik)}
        title={aciklama}
        aria-pressed={acik}
        className="inline-flex min-h-kontrol items-center gap-2 rounded-md border border-cetvel bg-girdi px-2.5 text-sm text-murekkep transition-colors duration-defter ease-orta hover:border-cetvel-koyu disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span aria-hidden="true">{acik ? "▣" : "▢"}</span>
        Bilgi Fişi
      </button>
      {!agentBagli ? (
        // KK-550: agent bulunamazsa fiş özelliği SESSİZCE devre dışı kalır,
        // hata ekranı gösterilmez.
        <Rozet tur="notr">agent yok</Rozet>
      ) : sonYazdirmaBasarili === false ? (
        <Rozet tur="uyari">son basım başarısız</Rozet>
      ) : null}
      {aciklama && <span className="text-xs text-murekkep-3">{aciklama}</span>}
    </div>
  );
}

/**
 * KK-811: offline maruziyet göstergesi.
 * Gerçek risk "tavan" değil, "tavan x offline terminal sayısı"dır.
 */
export function MaruziyetGostergesi({
  offlineTerminal,
  toplamTerminal,
  acikRisk,
}: {
  offlineTerminal: number;
  toplamTerminal: number;
  acikRisk: string;
}) {
  const risk = offlineTerminal > 0;
  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-lg border px-4 py-3 ${
        risk ? "border-uyari-cizgi bg-uyari-yumusak" : "border-cetvel bg-yuzey"
      }`}
    >
      <div>
        <p className="m-0 text-xs text-murekkep-3">Offline maruziyet</p>
        <p className="m-0 text-sm text-murekkep-2">
          <strong className="rakam font-mono">{offlineTerminal}</strong> /{" "}
          <span className="rakam font-mono">{toplamTerminal}</span> terminal
          çevrimdışı
        </p>
      </div>
      <div className="text-right">
        <p className="m-0 text-xs text-murekkep-3">Açık risk</p>
        <p className="tutar rakam m-0 font-mono text-lg font-semibold">
          {acikRisk} ₺
        </p>
      </div>
    </div>
  );
}
